import { useApolloClient, useMutation } from "@apollo/client/react";
import * as Dialog from "@radix-ui/react-dialog";
import { logger } from "@sa-nu/logger";
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { useCallback, useEffect, useRef, useState } from "react";

import { graphql } from "../../__generated__/gql";
import { useGtm } from "../../lib/google-tag-manager/gtm";
import { useSnackbar } from "../_contexts/snackbar";
import {
  STRIPE_ELEMENTS_APPEARANCE,
  useStripeClient,
} from "../_hooks/use-stripe-client";

const Mutation = graphql(`
  mutation CreateSetupIntentOnAccount {
    createStripeCardSetupIntentOfViewer {
      clientSecret
    }
  }
`);

const MutationForOrganization = graphql(`
  mutation CreateSetupIntentOnAccountOfOrganization($organizationId: ID!) {
    createStripeCardSetupIntentOfOrganization(organizationId: $organizationId) {
      clientSecret
    }
  }
`);

export type StripeCardSetDialogMessages = {
  dialogTitle: string;
  saveButton: string;
  cancelButton: string;
  setupIntentErrorAlert: string;
  cardRegisteredSnackbar: string;
  unknownErrorSnackbar: string;
};

const JA_MESSAGES: StripeCardSetDialogMessages = {
  dialogTitle: "カードの登録・更新",
  saveButton: "保存する",
  cancelButton: "キャンセル",
  setupIntentErrorAlert: "エラーが発生しました",
  cardRegisteredSnackbar: "カードを登録しました",
  unknownErrorSnackbar: "不明なエラーが発生しました",
};

export default function StripeCardSetDialog({
  open,
  onOpenChange,
  organizationId,
  messages = JA_MESSAGES,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // 指定した場合にはその組織のカードを、指定しない場合は個人のカードを更新。
  organizationId?: string;
  messages?: StripeCardSetDialogMessages;
}) {
  const [createSetupIntent] = useMutation(Mutation);
  const [createSetupIntentOfOrganization] = useMutation(
    MutationForOrganization,
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const stripe = useStripeClient();
  const gtm = useGtm();
  const fetchingRef = useRef(false);

  // ダイアログが開いた時に新しい SetupIntent を取得する
  useEffect(() => {
    if (!open || clientSecret !== null || fetchingRef.current) return;
    fetchingRef.current = true;

    if (organizationId) {
      createSetupIntentOfOrganization({ variables: { organizationId } }).then(
        (result) => {
          if (result.data) {
            setClientSecret(
              result.data.createStripeCardSetupIntentOfOrganization
                .clientSecret,
            );
          } else {
            alert(messages.setupIntentErrorAlert);
          }
        },
      );
    } else {
      createSetupIntent().then((result) => {
        if (result.data) {
          setClientSecret(
            result.data.createStripeCardSetupIntentOfViewer.clientSecret,
          );
        } else {
          alert(messages.setupIntentErrorAlert);
        }
      });
    }
  }, [
    open,
    clientSecret,
    organizationId,
    createSetupIntent,
    createSetupIntentOfOrganization,
    messages.setupIntentErrorAlert,
  ]);

  // open は親（CreditcardConsentGate）が起点で立てることがあり、その場合Radixの
  // onOpenChangeは発火しない。open propの変化を直接監視することで開始イベントを漏らさない。
  useEffect(() => {
    if (open) {
      gtm.sendEvent("card_register_started");
    }
  }, [open, gtm]);

  // ダイアログが閉じたら状態をリセットし、次回開いた時に新しい SetupIntent を取得する
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      onOpenChange(nextOpen);
      if (!nextOpen) {
        setClientSecret(null);
        fetchingRef.current = false;
      }
    },
    [onOpenChange],
  );

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="sd-overlay">
          <Dialog.Content
            className="sd-content w-[315px] md:w-[690px]"
            onPointerDownOutside={(e) => e.preventDefault()}
          >
            <Dialog.Title className="sd-title-header">
              {messages.dialogTitle}
            </Dialog.Title>
            <Dialog.Description asChild>
              <div className="old-dialog-sheet-body md:min-h-[360px]">
                {clientSecret && (
                  <Elements
                    key={clientSecret}
                    options={{
                      clientSecret,
                      appearance: STRIPE_ELEMENTS_APPEARANCE,
                    }}
                    stripe={stripe}
                  >
                    <CardForm
                      onUpdate={() => handleOpenChange(false)}
                      saveButtonLabel={messages.saveButton}
                      cancelButtonLabel={messages.cancelButton}
                      cardRegisteredSnackbar={messages.cardRegisteredSnackbar}
                      unknownErrorSnackbar={messages.unknownErrorSnackbar}
                    />
                  </Elements>
                )}
              </div>
            </Dialog.Description>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export function CardForm({
  onUpdate,
  saveButtonLabel,
  cancelButtonLabel,
  cardRegisteredSnackbar,
  unknownErrorSnackbar,
}: {
  onUpdate: () => void;
  saveButtonLabel: string;
  cancelButtonLabel: string;
  cardRegisteredSnackbar: string;
  unknownErrorSnackbar: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const showSnackBar = useSnackbar();
  const apolloClient = useApolloClient();
  const gtm = useGtm();
  // PaymentElement は iframe 内で非同期にマウントされるため、onReady が発火するまでは
  // stripe.confirmSetup() を呼ぶと IntegrationError になる。マウント完了まで送信をブロックする。
  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements || !paymentElementReady) return;

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      redirect: "if_required",
      confirmParams: {
        return_url: window.location.href,
      },
    });

    if (error) {
      // eslint-disable-next-line no-console
      console.log("Error:", error);
      showSnackBar({
        message: error.message || unknownErrorSnackbar,
        variant: "alert",
      });
    } else {
      switch (setupIntent.status) {
        case "succeeded": {
          gtm.sendEvent("card_registered");
          gtm.sendEvent("card_ready");
          showSnackBar({
            message: cardRegisteredSnackbar,
          });
          setTimeout(
            // 通常、背後でカード情報を表示しているので更新
            () => apolloClient.refetchQueries({ include: "active" }),
            // 直後にリクエストを送ると Stripe API レスポンスに最新のカードが反映されないので1.5秒後に取得
            1500,
          );
          onUpdate();
          break;
        }
        default: {
          showSnackBar({
            message: unknownErrorSnackbar,
            variant: "alert",
          });
          // eslint-disable-next-line no-console
          console.log("Unexpected setupIntent status:", setupIntent.status);
          break;
        }
      }
    }
  };

  return (
    <div className="">
      <div className="md:min-h-[284px]">
        <PaymentElement
          onReady={() => setPaymentElementReady(true)}
          onLoadError={(event) => {
            // iframe のロードに失敗した場合、onReady が発火せず送信ボタンが永久に
            // 無効化されたままになるので、ユーザーにスナックバーで通知する。
            // PII の流出を避けるため、StripeError の限定的なフィールドのみログ出力する。
            logger.warn("PaymentElement failed to load", {
              type: event.error.type,
              code: event.error.code,
              message: event.error.message,
            });
            showSnackBar({
              message: unknownErrorSnackbar,
              variant: "alert",
            });
          }}
        />
      </div>
      <div className="mt-[20px] flex flex-col gap-[10px] md:flex-row md:gap-[20px]">
        <button
          data-analytics-id="payment_method_submitted_save"
          type="button"
          className="btn-primary md:w-[70%]"
          disabled={!stripe || !elements || !paymentElementReady}
          onClick={handleSubmit}
        >
          {saveButtonLabel}
        </button>
        <Dialog.Close className="btn-secondary md:w-[30%]">
          {cancelButtonLabel}
        </Dialog.Close>
      </div>
    </div>
  );
}
